import { 
  Drill, Shield, Fuel, Wrench, BarChart3, Factory,
  Zap, Lock, Clock, Database, CheckCircle, Globe, 
  Brain, Target, Settings, Users, TrendingUp, Sparkles
} from 'lucide-react';
import { ProductConfigs } from './types';

export const oilGasSolutionsConfigs: ProductConfigs = {
  'drilling-operations-management': {
    keyBenefits: [
      {
        icon: Drill,
        title: 'Real-Time Drilling Intelligence',
        description: 'Advanced drilling operations monitoring with live data analytics, performance optimization, and predictive drilling insights.',
        metrics: '85% improvement in drilling efficiency',
        color: 'from-gray-500 to-slate-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Operations Optimization',
        description: 'Machine learning algorithms for drilling parameter optimization, formation analysis, and automated decision support.',
        metrics: '90% reduction in drilling incidents',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Performance Analytics',
        description: 'Comprehensive drilling performance metrics with cost analysis, efficiency tracking, and optimization recommendations.',
        metrics: '75% cost reduction achievement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Shield,
        title: 'Integrated Safety Management',
        description: 'Comprehensive safety protocols with risk assessment, incident prevention, and compliance monitoring systems.',
        metrics: '95% safety compliance improvement',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Wrench,
        title: 'Equipment Monitoring & Maintenance',
        description: 'Predictive maintenance system with equipment health monitoring, failure prediction, and maintenance optimization.',
        metrics: '80% equipment uptime improvement',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Multi-Site Operations Management',
        description: 'Centralized management for multiple drilling sites with unified dashboards and coordinated operations.',
        metrics: 'Unlimited site management',
        color: 'from-teal-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'Predictive Drilling Analytics',
        description: 'Advanced predictive modeling for drilling performance, formation prediction, and operational optimization.',
        metrics: 'Predictive drilling intelligence',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Automated Workflow Engine',
        description: 'Custom drilling workflows with automated processes, alert systems, and operational procedure management.',
        metrics: '90% workflow automation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Data Integration',
        description: 'Unified data platform integrating geological, drilling, and operational data for complete visibility.',
        metrics: '360-degree operational visibility',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Expert System Integration',
        description: 'Integration with drilling expert systems and knowledge bases for intelligent decision support.',
        metrics: 'Expert-level decision support',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Benchmarking',
        description: 'Industry benchmarking with performance comparison, best practice identification, and improvement tracking.',
        metrics: 'Industry-leading performance',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Sparkles,
        title: 'Advanced Visualization Suite',
        description: 'Interactive 3D visualization with real-time drilling progress, geological modeling, and operational dashboards.',
        metrics: 'Immersive operational insights',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Industrial Security Standards',
        description: 'Advanced cybersecurity with OT/IT convergence protection, threat detection, and industrial network security.',
        metrics: 'Military-grade industrial security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Operations Processing',
        description: 'High-performance real-time data processing with edge computing and instant response capabilities.',
        metrics: 'Millisecond response times',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Remote Operations',
        description: 'Encrypted remote access with secure VPN, multi-factor authentication, and controlled operational access.',
        metrics: 'Bank-level operational security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'Critical Operations Reliability',
        description: 'Mission-critical system reliability with redundancy, failover, and 99.99% uptime guarantee.',
        metrics: '99.99% critical system uptime',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Secure Data Management',
        description: 'Protected operational data with encrypted storage, secure backup, and disaster recovery capabilities.',
        metrics: '100% data integrity assurance',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Compliance & Audit Ready',
        description: 'Comprehensive regulatory compliance with audit trails, safety reporting, and environmental monitoring.',
        metrics: '100% regulatory compliance',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'hse-management-system': {
    keyBenefits: [
      {
        icon: Shield,
        title: 'Comprehensive HSE Framework',
        description: 'Complete health, safety, and environmental management with integrated risk assessment and compliance monitoring.',
        metrics: '95% reduction in safety incidents',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Risk Assessment',
        description: 'Intelligent risk analysis with predictive safety modeling, hazard identification, and prevention strategies.',
        metrics: '90% accident prevention rate',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Advanced Safety Training',
        description: 'Comprehensive safety training programs with VR simulations, competency tracking, and certification management.',
        metrics: '100% safety training compliance',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Environmental Monitoring',
        description: 'Real-time environmental monitoring with emissions tracking, compliance reporting, and impact assessment.',
        metrics: '85% environmental compliance improvement',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Target,
        title: 'Incident Management System',
        description: 'Complete incident lifecycle management with reporting, investigation, corrective actions, and prevention.',
        metrics: '80% faster incident resolution',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Global HSE Standards',
        description: 'International HSE compliance with multi-region standards, local regulations, and global best practices.',
        metrics: 'Worldwide HSE compliance',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'Predictive Safety Analytics',
        description: 'Advanced analytics for safety trend prediction, risk forecasting, and proactive intervention strategies.',
        metrics: 'Predictive safety intelligence',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom HSE Workflows',
        description: 'Configurable HSE processes with automated workflows, approval chains, and compliance tracking.',
        metrics: 'Unlimited workflow flexibility',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Database,
        title: 'Integrated Safety Database',
        description: 'Comprehensive safety data repository with historical analysis, trend identification, and knowledge management.',
        metrics: 'Complete safety knowledge base',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Audit & Inspection Management',
        description: 'Automated audit processes with checklist management, finding tracking, and corrective action monitoring.',
        metrics: '90% audit efficiency improvement',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Safety Performance Optimization',
        description: 'Continuous safety improvement with performance benchmarking, best practice identification, and optimization.',
        metrics: 'Continuous safety enhancement',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Factory,
        title: 'Operational Safety Integration',
        description: 'Deep integration with operational systems for real-time safety monitoring and automated safety responses.',
        metrics: 'Integrated safety ecosystem',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Safety Data Security',
        description: 'Protected safety information with encrypted storage, controlled access, and comprehensive audit trails.',
        metrics: 'Complete safety data protection',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Safety Monitoring',
        description: 'Instant safety event processing with real-time alerts, automated responses, and immediate escalation.',
        metrics: 'Real-time safety response',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Compliance Data Protection',
        description: 'Secure compliance management with protected reporting, controlled access, and regulatory data security.',
        metrics: 'Regulatory-grade data security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'Critical Safety System Reliability',
        description: 'Mission-critical reliability with redundant systems, automatic failover, and guaranteed availability.',
        metrics: '99.99% safety system uptime',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Secure HSE Data Management',
        description: 'Protected HSE data with encrypted storage, secure backup, and disaster recovery capabilities.',
        metrics: '100% HSE data integrity',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Safety Quality Assurance',
        description: 'Automated safety quality control with validation checks, accuracy verification, and continuous improvement.',
        metrics: '99.9% safety data accuracy',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'pipeline-integrity-management': {
    keyBenefits: [
      {
        icon: Fuel,
        title: 'Advanced Pipeline Monitoring',
        description: 'Comprehensive pipeline integrity monitoring with real-time sensor data, leak detection, and condition assessment.',
        metrics: '99% leak detection accuracy',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Integrity Analysis',
        description: 'Machine learning for corrosion prediction, failure analysis, and intelligent maintenance planning.',
        metrics: '90% failure prediction accuracy',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Risk Assessment & Management',
        description: 'Comprehensive risk analysis with threat assessment, vulnerability evaluation, and mitigation strategies.',
        metrics: '85% risk reduction achievement',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Integrity Performance Analytics',
        description: 'Advanced analytics for pipeline performance, integrity trends, and optimization recommendations.',
        metrics: 'Data-driven integrity insights',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Geospatial Pipeline Mapping',
        description: 'Interactive geospatial mapping with GIS integration, route visualization, and spatial analysis capabilities.',
        metrics: 'Complete spatial visibility',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Shield,
        title: 'Compliance & Regulatory Management',
        description: 'Automated compliance tracking with regulatory reporting, inspection management, and documentation.',
        metrics: '100% regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'Predictive Integrity Modeling',
        description: 'Advanced predictive models for integrity degradation, remaining life assessment, and failure forecasting.',
        metrics: 'Predictive integrity intelligence',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Integrity Workflows',
        description: 'Configurable integrity management processes with automated inspections, maintenance scheduling, and response protocols.',
        metrics: '90% workflow automation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Data Integration',
        description: 'Unified integrity data platform with inspection data, sensor readings, and operational information.',
        metrics: '360-degree integrity visibility',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Wrench,
        title: 'Maintenance Optimization',
        description: 'Intelligent maintenance planning with predictive scheduling, resource optimization, and cost management.',
        metrics: '75% maintenance cost reduction',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Integrity Performance Benchmarking',
        description: 'Industry benchmarking with performance comparison, best practice identification, and continuous improvement.',
        metrics: 'Industry-leading integrity performance',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Emergency Response Integration',
        description: 'Integrated emergency response with automated alerts, response procedures, and crisis management.',
        metrics: 'Rapid emergency response',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Critical Infrastructure Security',
        description: 'Advanced cybersecurity for critical pipeline infrastructure with threat protection and secure operations.',
        metrics: 'Critical infrastructure protection',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Monitoring Performance',
        description: 'High-performance real-time monitoring with instant data processing and immediate alert generation.',
        metrics: 'Real-time integrity monitoring',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Pipeline Data',
        description: 'Protected pipeline information with encrypted data, secure communications, and controlled access.',
        metrics: 'Military-grade pipeline security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'Mission-Critical Reliability',
        description: 'Ultra-reliable monitoring system with redundancy, failover capabilities, and guaranteed uptime.',
        metrics: '99.99% monitoring system uptime',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Secure Integrity Data Management',
        description: 'Protected integrity data with encrypted storage, secure backup, and disaster recovery systems.',
        metrics: '100% integrity data protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Fuel,
        title: 'Pipeline Quality Assurance',
        description: 'Comprehensive quality control with data validation, accuracy verification, and continuous monitoring.',
        metrics: '99.9% monitoring accuracy',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'equipment-maintenance-optimization': {
    keyBenefits: [
      {
        icon: Wrench,
        title: 'Predictive Maintenance Intelligence',
        description: 'Advanced predictive maintenance with machine learning algorithms for failure prediction and optimal scheduling.',
        metrics: '80% reduction in unplanned downtime',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Brain,
        title: 'AI Equipment Health Monitoring',
        description: 'Intelligent equipment condition monitoring with anomaly detection, performance analysis, and health scoring.',
        metrics: '95% equipment health visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Maintenance Performance Analytics',
        description: 'Comprehensive maintenance analytics with cost optimization, efficiency tracking, and ROI measurement.',
        metrics: '70% maintenance cost reduction',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Optimized Maintenance Scheduling',
        description: 'Intelligent maintenance planning with resource optimization, downtime minimization, and productivity maximization.',
        metrics: '90% maintenance efficiency improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Asset Management',
        description: 'Complete equipment lifecycle management with asset tracking, performance monitoring, and optimization.',
        metrics: 'Total asset visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Mobile Maintenance Platform',
        description: 'Mobile-first maintenance operations with field access, real-time updates, and technician collaboration.',
        metrics: 'Enhanced field productivity',
        color: 'from-teal-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'Machine Learning Optimization',
        description: 'Advanced ML algorithms for maintenance optimization, pattern recognition, and continuous improvement.',
        metrics: 'Self-optimizing maintenance',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Maintenance Workflows',
        description: 'Configurable maintenance processes with automated work orders, approval chains, and quality control.',
        metrics: 'Unlimited workflow customisation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Globe,
        title: 'Multi-Site Maintenance Management',
        description: 'Centralized maintenance operations across multiple sites with unified dashboards and resource coordination.',
        metrics: 'Global maintenance coordination',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Spare Parts Optimization',
        description: 'Intelligent spare parts management with demand forecasting, inventory optimization, and supply chain integration.',
        metrics: '60% inventory cost reduction',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Maintenance Benchmarking',
        description: 'Industry benchmarking with performance comparison, best practice identification, and continuous improvement.',
        metrics: 'Industry-leading maintenance performance',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Factory,
        title: 'IoT Sensor Integration',
        description: 'Advanced IoT integration with sensor networks, edge computing, and real-time condition monitoring.',
        metrics: 'Complete IoT connectivity',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Maintenance Data Security',
        description: 'Secure maintenance operations with protected data, controlled access, and comprehensive audit trails.',
        metrics: 'Complete maintenance security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Maintenance Processing',
        description: 'High-performance maintenance operations with instant data processing and real-time decision support.',
        metrics: 'Real-time maintenance intelligence',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Equipment Data',
        description: 'Protected equipment information with encrypted data, secure communications, and role-based access.',
        metrics: 'Bank-level equipment security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'High-Availability Maintenance System',
        description: 'Reliable maintenance platform with redundancy, failover capabilities, and guaranteed system availability.',
        metrics: '99.99% maintenance system uptime',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Secure Asset Data Management',
        description: 'Protected asset information with encrypted storage, secure backup, and disaster recovery capabilities.',
        metrics: '100% asset data protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Wrench,
        title: 'Maintenance Quality Assurance',
        description: 'Automated quality control with maintenance validation, performance verification, and continuous improvement.',
        metrics: '99.9% maintenance quality',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'production-data-analytics': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Advanced Production Analytics',
        description: 'Comprehensive production intelligence with real-time analytics, performance dashboards, and optimization insights.',
        metrics: '85% production optimization improvement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Production Intelligence',
        description: 'Machine learning for production optimization, demand forecasting, and intelligent process automation.',
        metrics: '90% production prediction accuracy',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: TrendingUp,
        title: 'Real-Time Performance Monitoring',
        description: 'Live production monitoring with instant performance tracking, trend analysis, and alert systems.',
        metrics: 'Real-time production visibility',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Production Optimization Engine',
        description: 'Intelligent production optimization with efficiency maximization, cost reduction, and quality improvement.',
        metrics: '75% efficiency improvement',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Multi-Site Production Analytics',
        description: 'Centralized analytics across multiple production sites with unified dashboards and comparative analysis.',
        metrics: 'Global production intelligence',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Data Integration',
        description: 'Unified production data platform with real-time integration, historical analysis, and predictive modeling.',
        metrics: '360-degree production visibility',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'Predictive Production Modeling',
        description: 'Advanced predictive models for production forecasting, demand planning, and resource optimization.',
        metrics: 'Predictive production intelligence',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Analytics Framework',
        description: 'Configurable analytics with custom KPIs, personalized dashboards, and flexible reporting capabilities.',
        metrics: 'Unlimited analytics customisation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Collaborative Analytics Platform',
        description: 'Team-based analytics with shared dashboards, collaborative insights, and cross-functional reporting.',
        metrics: 'Enhanced team collaboration',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Quality Analytics Integration',
        description: 'Integrated quality analytics with production metrics, defect analysis, and quality optimization.',
        metrics: '95% quality improvement',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Factory,
        title: 'IoT Production Analytics',
        description: 'Advanced IoT analytics with sensor integration, edge computing, and real-time production intelligence.',
        metrics: 'Complete IoT production insights',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Wrench,
        title: 'Maintenance Analytics Integration',
        description: 'Integrated maintenance analytics with production correlation, downtime analysis, and optimization insights.',
        metrics: 'Integrated operations analytics',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Production Data Security',
        description: 'Secure production analytics with protected data, controlled access, and comprehensive security measures.',
        metrics: 'Military-grade production security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Analytics Engine',
        description: 'High-performance analytics with instant data processing, real-time calculations, and immediate insights.',
        metrics: 'Millisecond analytics response',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Analytics Environment',
        description: 'Protected analytics platform with encrypted data, secure access, and privacy-compliant processing.',
        metrics: 'Bank-level analytics security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'High-Performance Analytics Platform',
        description: 'Scalable analytics infrastructure with distributed processing, load balancing, and optimal performance.',
        metrics: '99.99% analytics availability',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Big Data Analytics Infrastructure',
        description: 'Massive data processing capabilities with data lake architecture and advanced analytics engines.',
        metrics: 'Petabyte-scale analytics',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Analytics Quality Assurance',
        description: 'Automated quality control with data validation, accuracy verification, and continuous improvement.',
        metrics: '99.9% analytics accuracy',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  }
};